import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Car, Plus, Edit, Trash2, ArrowLeft, Loader2, Check, X, Search, Sparkles, Truck, ChevronDown
} from 'lucide-react';

export default function RoadsideVehiclesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookingUpVin, setLookingUpVin] = useState(false);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // VIN decode result info
  const [vinDecodeInfo, setVinDecodeInfo] = useState(null);
  
  // Show heavy duty options toggle
  const [showHeavyDuty, setShowHeavyDuty] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    license_plate: '',
    vin: '',
    notes: '',
    // Heavy-duty truck options
    vehicle_category: '',
    axle_config: '',
    sleeper_type: '',
    trailer_compatibility: [],
    weight_class: ''
  });
  
  // Options for heavy-duty fields
  const vehicleCategories = [
    { value: 'passenger', label: 'Passenger Vehicle' },
    { value: 'pickup', label: 'Pickup Truck' },
    { value: 'straight_truck', label: 'Straight Truck' },
    { value: 'tractor_truck', label: 'Tractor Truck (Semi)' },
    { value: 'trailer', label: 'Trailer' },
    { value: 'bus', label: 'Bus' }
  ];
  
  const axleConfigs = [
    { value: '4x2', label: '4x2 (Single Drive Axle)' },
    { value: '4x4', label: '4x4 (All-Wheel Drive)' },
    { value: '6x2', label: '6x2 (Lift Axle)' },
    { value: '6x4', label: '6x4 (Tandem Drive)' },
    { value: '6x6', label: '6x6 (All-Wheel)' },
    { value: '8x4', label: '8x4 (Quad Axle)' }
  ];
  
  const sleeperTypes = [
    { value: 'day_cab', label: 'Day Cab' },
    { value: 'flat_top', label: 'Flat Top Sleeper' },
    { value: 'mid_roof', label: 'Mid-Roof Sleeper' },
    { value: 'raised_roof', label: 'Raised Roof Sleeper' },
    { value: 'studio_sleeper', label: 'Studio Sleeper' }
  ];
  
  const trailerTypes = [
    { value: 'dry_van', label: 'Dry Van' },
    { value: 'reefer', label: 'Refrigerated (Reefer)' },
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'tanker', label: 'Tanker' },
    { value: 'lowboy', label: 'Lowboy' },
    { value: 'container', label: 'Container Chassis' },
    { value: 'car_hauler', label: 'Car Hauler' },
    { value: 'dump', label: 'Dump Trailer' }
  ];
  
  const weightClasses = [
    { value: 'class_1', label: 'Class 1 (up to 6,000 lbs)' },
    { value: 'class_2', label: 'Class 2 (6,001-10,000 lbs)' },
    { value: 'class_3', label: 'Class 3 (10,001-14,000 lbs)' },
    { value: 'class_4', label: 'Class 4 (14,001-16,000 lbs)' },
    { value: 'class_5', label: 'Class 5 (16,001-19,500 lbs)' },
    { value: 'class_6', label: 'Class 6 (19,501-26,000 lbs)' },
    { value: 'class_7', label: 'Class 7 (26,001-33,000 lbs)' },
    { value: 'class_8', label: 'Class 8 (33,001+ lbs)' }
  ];

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const res = await api.get('/roadside/vehicles');
      setVehicles(res.data || []);
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      make: '',
      model: '',
      year: '',
      color: '',
      license_plate: '',
      vin: '',
      notes: '',
      vehicle_category: '',
      axle_config: '',
      sleeper_type: '',
      trailer_compatibility: [],
      weight_class: ''
    });
    setVinDecodeInfo(null);
    setShowHeavyDuty(false);
  };

  const openAddDialog = () => {
    setEditingVehicle(null);
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    const hasHeavyDutyInfo = vehicle.vehicle_category || vehicle.axle_config || 
                             vehicle.sleeper_type || vehicle.weight_class ||
                             (vehicle.trailer_compatibility && vehicle.trailer_compatibility.length > 0);
    setShowHeavyDuty(hasHeavyDutyInfo);
    setForm({
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year?.toString() || '',
      color: vehicle.color || '',
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      notes: vehicle.notes || '',
      vehicle_category: vehicle.vehicle_category || '',
      axle_config: vehicle.axle_config || '',
      sleeper_type: vehicle.sleeper_type || '',
      trailer_compatibility: vehicle.trailer_compatibility || [],
      weight_class: vehicle.weight_class || ''
    });
    setVinDecodeInfo(null);
    setShowDialog(true);
  };

  const lookupVin = async () => {
    const vin = form.vin.trim();
    if (!vin) {
      toast.error('Please enter a VIN to lookup');
      return;
    }
    if (vin.length < 11) {
      toast.error('VIN must be at least 11 characters');
      return;
    }
    
    setLookingUpVin(true);
    setVinDecodeInfo(null);
    
    try {
      const res = await api.get(`/roadside/vehicles/vin-decode/${vin}`);
      const data = res.data;
      
      // Auto-fill form fields
      setForm(prev => ({
        ...prev,
        make: data.make || prev.make,
        model: data.model || prev.model,
        year: data.year?.toString() || prev.year,
        vin: data.vin || prev.vin
      }));
      
      // Store additional info for display (including heavy-duty data)
      setVinDecodeInfo({
        vehicle_type: data.vehicle_type,
        body_class: data.body_class,
        drive_type: data.drive_type,
        fuel_type: data.fuel_type,
        engine_cylinders: data.engine_cylinders,
        displacement_liters: data.displacement_liters,
        manufacturer: data.manufacturer,
        // Heavy-duty fields
        gvwr: data.gvwr,
        axles: data.axles,
        cab_type: data.cab_type,
        trailer_type: data.trailer_type
      });
      
      // If it's a truck, auto-show heavy duty options
      const isTruck = data.vehicle_type?.toLowerCase().includes('truck') ||
                      data.body_class?.toLowerCase().includes('truck') ||
                      data.gvwr;
      if (isTruck) {
        setShowHeavyDuty(true);
      }
      
      toast.success(`Found: ${data.year} ${data.make} ${data.model}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to decode VIN';
      toast.error(msg);
    }
    setLookingUpVin(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.make || !form.model || !form.year || !form.color) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const yearNum = parseInt(form.year);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) {
      toast.error('Please enter a valid year');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        make: form.make,
        model: form.model,
        year: yearNum,
        color: form.color,
        license_plate: form.license_plate || null,
        vin: form.vin || null,
        notes: form.notes || null,
        // Heavy-duty truck options
        vehicle_category: form.vehicle_category || null,
        axle_config: form.axle_config || null,
        sleeper_type: form.sleeper_type || null,
        trailer_compatibility: form.trailer_compatibility?.length > 0 ? form.trailer_compatibility : null,
        weight_class: form.weight_class || null
      };
      
      if (editingVehicle) {
        // Update existing vehicle
        await api.put(`/roadside/vehicles/${editingVehicle.id}`, payload);
        toast.success('Vehicle updated');
      } else {
        // Create new vehicle
        await api.post('/roadside/vehicles', payload);
        toast.success('Vehicle added');
      }
      
      setShowDialog(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    }
    setSaving(false);
  };

  const handleDelete = async (vehicleId) => {
    try {
      await api.delete(`/roadside/vehicles/${vehicleId}`);
      toast.success('Vehicle deleted');
      setDeleteConfirm(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="vehicles-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/roadside')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">My Vehicles</h1>
            <p className="text-slate-400 text-sm">Manage your saved vehicles for roadside assistance</p>
          </div>
          <Button onClick={openAddDialog} data-testid="add-vehicle-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        </div>
        
        {/* Vehicles List */}
        {vehicles.length === 0 ? (
          <Card className="border-dashed" data-testid="empty-state">
            <CardContent className="p-8 text-center">
              <Car className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h2 className="text-lg font-medium mb-2">No Saved Vehicles</h2>
              <p className="text-slate-400 text-sm mb-4">
                Add your vehicles to quickly select them when requesting roadside assistance.
              </p>
              <Button onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Vehicle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="vehicles-list">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="hover:border-slate-600 transition-colors" data-testid={`vehicle-card-${vehicle.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      vehicle.vehicle_category === 'tractor_truck' || vehicle.vehicle_category === 'straight_truck'
                        ? 'bg-orange-500/10 border border-orange-500/20'
                        : 'bg-blue-500/10 border border-blue-500/20'
                    }`}>
                      {vehicle.vehicle_category === 'tractor_truck' || vehicle.vehicle_category === 'straight_truck' ? (
                        <Truck className="h-6 w-6 text-orange-400" />
                      ) : (
                        <Car className="h-6 w-6 text-blue-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {vehicle.color}
                        </Badge>
                        {vehicle.vehicle_category && vehicle.vehicle_category !== 'passenger' && (
                          <Badge variant="secondary" className="text-xs bg-orange-500/20 text-orange-300">
                            {vehicleCategories.find(c => c.value === vehicle.vehicle_category)?.label || vehicle.vehicle_category}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                        {vehicle.license_plate && (
                          <span>Plate: <span className="text-slate-300">{vehicle.license_plate}</span></span>
                        )}
                        {vehicle.vin && (
                          <span>VIN: <span className="text-slate-300">{vehicle.vin.slice(-6)}</span></span>
                        )}
                        {vehicle.axle_config && (
                          <span>Axles: <span className="text-slate-300">{vehicle.axle_config}</span></span>
                        )}
                        {vehicle.weight_class && (
                          <span>Class: <span className="text-slate-300">{vehicle.weight_class.replace('class_', '')}</span></span>
                        )}
                      </div>
                      
                      {vehicle.notes && (
                        <p className="text-sm text-slate-500 mt-2 line-clamp-1">{vehicle.notes}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(vehicle)}
                        data-testid={`edit-vehicle-${vehicle.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => setDeleteConfirm(vehicle)}
                        data-testid={`delete-vehicle-${vehicle.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
              </DialogTitle>
              <DialogDescription>
                {editingVehicle 
                  ? 'Update your vehicle details below.'
                  : 'Enter your VIN to auto-fill details, or enter manually.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {/* VIN Lookup Section - Prominent at top */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                  <Sparkles className="h-4 w-4" />
                  Quick Fill with VIN
                </div>
                <div className="flex gap-2">
                  <Input
                    value={form.vin}
                    onChange={(e) => setForm({...form, vin: e.target.value.toUpperCase()})}
                    placeholder="Enter full 17-character VIN"
                    maxLength={17}
                    className="font-mono uppercase"
                    data-testid="input-vin"
                  />
                  <Button 
                    type="button"
                    onClick={lookupVin}
                    disabled={lookingUpVin || form.vin.length < 11}
                    className="shrink-0"
                    data-testid="lookup-vin-btn"
                  >
                    {lookingUpVin ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-2 hidden sm:inline">Lookup</span>
                  </Button>
                </div>
                {form.vin && form.vin.length < 17 && (
                  <p className="text-xs text-slate-400">
                    {17 - form.vin.length} more characters needed for full VIN
                  </p>
                )}
              </div>
              
              {/* VIN Decode Info Display */}
              {vinDecodeInfo && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400 text-sm font-medium mb-2">
                    <Check className="h-4 w-4" />
                    Vehicle Identified via NHTSA
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400">
                    {vinDecodeInfo.vehicle_type && (
                      <span>Type: <span className="text-slate-300">{vinDecodeInfo.vehicle_type}</span></span>
                    )}
                    {vinDecodeInfo.body_class && (
                      <span>Body: <span className="text-slate-300">{vinDecodeInfo.body_class}</span></span>
                    )}
                    {vinDecodeInfo.fuel_type && (
                      <span>Fuel: <span className="text-slate-300">{vinDecodeInfo.fuel_type}</span></span>
                    )}
                    {vinDecodeInfo.drive_type && (
                      <span>Drive: <span className="text-slate-300">{vinDecodeInfo.drive_type}</span></span>
                    )}
                    {vinDecodeInfo.engine_cylinders && (
                      <span>Cylinders: <span className="text-slate-300">{vinDecodeInfo.engine_cylinders}</span></span>
                    )}
                    {vinDecodeInfo.displacement_liters && (
                      <span>Engine: <span className="text-slate-300">{vinDecodeInfo.displacement_liters}L</span></span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-900 px-2 text-slate-500">Vehicle Details</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Make *</Label>
                  <Input
                    value={form.make}
                    onChange={(e) => setForm({...form, make: e.target.value})}
                    placeholder="Toyota"
                    data-testid="input-make"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Model *</Label>
                  <Input
                    value={form.model}
                    onChange={(e) => setForm({...form, model: e.target.value})}
                    placeholder="Camry"
                    data-testid="input-model"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Year *</Label>
                  <Input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({...form, year: e.target.value})}
                    placeholder="2020"
                    data-testid="input-year"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color *</Label>
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({...form, color: e.target.value})}
                    placeholder="Silver"
                    data-testid="input-color"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>License Plate</Label>
                <Input
                  value={form.license_plate}
                  onChange={(e) => setForm({...form, license_plate: e.target.value.toUpperCase()})}
                  placeholder="ABC1234"
                  className="uppercase"
                  data-testid="input-plate"
                />
              </div>
              
              {/* Heavy-Duty Truck Options - Collapsible */}
              <Collapsible open={showHeavyDuty} onOpenChange={setShowHeavyDuty}>
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-between p-3 h-auto border border-slate-700 hover:bg-slate-800"
                    data-testid="heavy-duty-toggle"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4 text-orange-400" />
                      <span>Heavy-Duty Truck Options</span>
                      {(form.vehicle_category || form.axle_config || form.weight_class) && (
                        <Badge variant="secondary" className="text-xs">Configured</Badge>
                      )}
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showHeavyDuty ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* GVWR Info from VIN if available */}
                  {vinDecodeInfo?.gvwr && (
                    <div className="p-2 bg-orange-500/10 border border-orange-500/20 rounded text-xs text-orange-300">
                      <span className="font-medium">NHTSA GVWR:</span> {vinDecodeInfo.gvwr}
                      {vinDecodeInfo.axles && <span className="ml-3"><span className="font-medium">Axles:</span> {vinDecodeInfo.axles}</span>}
                      {vinDecodeInfo.cab_type && <span className="ml-3"><span className="font-medium">Cab:</span> {vinDecodeInfo.cab_type}</span>}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Vehicle Category</Label>
                      <Select 
                        value={form.vehicle_category} 
                        onValueChange={(v) => setForm({...form, vehicle_category: v})}
                      >
                        <SelectTrigger data-testid="select-category">
                          <SelectValue placeholder="Select type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleCategories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Weight Class</Label>
                      <Select 
                        value={form.weight_class} 
                        onValueChange={(v) => setForm({...form, weight_class: v})}
                      >
                        <SelectTrigger data-testid="select-weight">
                          <SelectValue placeholder="Select class..." />
                        </SelectTrigger>
                        <SelectContent>
                          {weightClasses.map(wc => (
                            <SelectItem key={wc.value} value={wc.value}>{wc.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Axle Configuration</Label>
                      <Select 
                        value={form.axle_config} 
                        onValueChange={(v) => setForm({...form, axle_config: v})}
                      >
                        <SelectTrigger data-testid="select-axle">
                          <SelectValue placeholder="Select axles..." />
                        </SelectTrigger>
                        <SelectContent>
                          {axleConfigs.map(ax => (
                            <SelectItem key={ax.value} value={ax.value}>{ax.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Sleeper Type</Label>
                      <Select 
                        value={form.sleeper_type} 
                        onValueChange={(v) => setForm({...form, sleeper_type: v})}
                      >
                        <SelectTrigger data-testid="select-sleeper">
                          <SelectValue placeholder="Select sleeper..." />
                        </SelectTrigger>
                        <SelectContent>
                          {sleeperTypes.map(st => (
                            <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Trailer Compatibility</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {trailerTypes.map(tt => (
                        <label 
                          key={tt.value} 
                          className="flex items-center gap-2 text-sm cursor-pointer hover:text-white"
                        >
                          <Checkbox
                            checked={form.trailer_compatibility?.includes(tt.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setForm({...form, trailer_compatibility: [...(form.trailer_compatibility || []), tt.value]});
                              } else {
                                setForm({...form, trailer_compatibility: form.trailer_compatibility?.filter(t => t !== tt.value) || []});
                              }
                            }}
                            data-testid={`trailer-${tt.value}`}
                          />
                          <span className="text-slate-300">{tt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  placeholder="Any special notes about this vehicle..."
                  data-testid="input-notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {editingVehicle ? 'Save Changes' : 'Add Vehicle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-400">Delete Vehicle?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete your{' '}
                <span className="text-white font-medium">
                  {deleteConfirm?.year} {deleteConfirm?.make} {deleteConfirm?.model}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(deleteConfirm.id)}
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
